import {
  Component,
  OnChanges,
  ChangeDetectorRef, ViewChildren, QueryList, Output, EventEmitter, ViewChild, OnInit, OnDestroy, Input,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import * as _ from 'lodash';

import { TableComponent } from 'shared/table/table/table.component';
import { PriceSheet } from '../price-sheet.model';
import * as utils from 'utils/utils';
import { PRICE_SHEETS_NAME } from '../../../variables';
import { fieldsInfo } from '../fields-info';
import { TableEditableComponent } from 'shared/table/table-editable/table-editable.component';
import {ProductPanelComponent} from '../../../../../product-panel/product-panel.component';
import {rowData, contentData, collapseWidthPx, collapseWidthPercents, defaultPriceSheetWidth} from '../table-data';
import {DomSanitizer} from '@angular/platform-browser';
import {CollapseGroupComponent} from 'shared/collapse/collapse-group/collapse-group.component';
import {LoaderComponent} from 'shared/loader/loader.component';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';


@Component({
  moduleId: module.id,
  selector: 'price-sheets-form',
  templateUrl: 'price-sheets-form.component.html',
  styleUrls: ['price-sheets-form.component.scss'],
})
export class PriceSheetsFormComponent extends TableComponent implements OnChanges, OnInit, OnDestroy {
  @Input() savingEvents: Observable<void>;

  @ViewChild(TableEditableComponent, {static: false}) tableEditable: TableEditableComponent | void;
  @ViewChildren(CollapseGroupComponent) collapseGroups: QueryList<CollapseGroupComponent>;

  rowData: any[] = rowData;
  contentData: any[] = contentData;
  collapseWidth = collapseWidthPercents;
  defaultPriceSheetWidth = defaultPriceSheetWidth;
  controlClassPrefix: string = 'price-sheets-control-row-';
  editMode: boolean = false;

  headers: any[] = [
    'price_sheet_name',
    'price_sheet_number',
    'currency_code',
    'effective_date'
  ];

  sourceData: any;
  fieldsInfo: any = fieldsInfo;
  priceData: Array<any>;
  quarteredData: Array<any>;
  form: UntypedFormGroup;
  private saveInProgress: boolean = false;
  @ViewChild(LoaderComponent, {static: true})
  loader: LoaderComponent;

  @Output() cancel: EventEmitter<any> = new EventEmitter();
  @Output() saveChanges: EventEmitter<any> = new EventEmitter();

  private savingEventsSubscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    protected ref: ChangeDetectorRef,
    protected sanitizer: DomSanitizer,
  ) {
    super();
    this.form = this.fb.group({
      priceSheets: this.fb.array([])
    });
  }

  get priceSheets(): UntypedFormArray {
    return this.form.get('priceSheets') as UntypedFormArray;
  }

  get isEmpty(): boolean {
    return !_.get(this.priceSheets, 'controls.length');
  }

  get formArray(): UntypedFormArray {
    return this.priceSheets;
  }

  get isEditMode(): boolean {
    return this.editMode;
  }

  set isEditMode(value: boolean) {
    this.editMode = value;
  }

  toggleEditMode() {
    this.editMode = !this.editMode;
  }

  get isFormChanged(): boolean {
    if (this.isEditMode) {
      return !!this.form.dirty;
    } else {
      return false;
    }
  }

  ngOnInit() {
    this.savingEventsSubscription = this.savingEvents.subscribe(() => {
      this.editMode = false;
    });
  }

  ngOnChanges() {
    this.setData(this.data);
  }

  ngOnDestroy() {
    this.savingEventsSubscription.unsubscribe();
  }

  onAddNewPriceSheetClick() {
    const model = utils.populateModel(PriceSheet, this.segmentData, {});
    _.set(model, 'isNewRow', new UntypedFormControl(true));
    _.set(model, 'primary', new UntypedFormControl(false));
    _.set(model, 'secondary', new UntypedFormControl(false));
    _.set(model, 'currencyCode', new UntypedFormControl([]));
    this.priceSheets.push(this.fb.group(model));
    this.form.markAsDirty();
  }

  onCheckboxChange(event: any, formGroup: UntypedFormGroup, controlName: string) {
    const control = formGroup.get(controlName);
    if (event.target.checked) {
      _.each(this.priceSheets.controls, (_formGroup: UntypedFormGroup) => {
          _formGroup.get(controlName).setValue(false);
      });
    }
    // checked price item cannot be and primary and secondary simultaneously
    const otherDefaultControl = formGroup.get(
      (controlName === 'primary') ? 'secondary' : 'primary'
    );
    if (otherDefaultControl.value && event.target.checked) {
      otherDefaultControl.setValue(false);
    }
    control.setValue(event.target.checked);
    this.form.markAsDirty();
  }

  onDateChange(selectedDate: string, item: any, formGroup: UntypedFormGroup, index: number) {
    if (item.item_name === 'expiration_date') {
      this.handleDateAheadChange(
        formGroup.get('effectiveDate'),
        'effective_date',
        selectedDate,
        index,
      );
    }
  }

  onSelectChange(event: Array<any> = [], fieldName: string, formGroup: UntypedFormGroup, index: number) {
    if (fieldName !== 'currencyCode') { return; }
    const selectedOptions = event.map(item => item.option);
    formGroup.get('currencyCode').setValue(selectedOptions);
  }

  isDate(itemName: string): boolean {
    return _.includes(this.fieldsInfo.dateFields, itemName);
  }

  public getField(fieldMap: string): any {
    // @ts-ignore
    return _.find(this.segmentData, {field_map: fieldMap});
  }

  public getFieldTitle(fieldMap: string): any {
    const item = this.getField(fieldMap);
    return item ? item.title : '';
  }

  setData(data: any) {
    if (!data) { return; }
    this.sourceData = [...data];
    this.separateFields(this.segmentData);
    this.setFormArray(data, this.segmentData);
    _.defer(() => {
      this.setFormDateErrors();
      this.ref.markForCheck();
    });
  }

  separateFields(data: any) {
    if (this.priceData) { return; }
    this.priceData = _.filter(data, (item: any) => {
      return _.includes(this.fieldsInfo.priceFields, item.item_name);
    });
    this.quarteredData = _.filter(data, (item: any) => {
      return _.includes(this.fieldsInfo.quarteredSizeFields, item.item_name);
    });
  }

  setFormArray(data: any, segmentData: any): void {
    const formGroups = _.map(data, (item: any, index: number) => {
      const model = utils.populateModel(PriceSheet, segmentData, item);
      _.set(model, 'isNewRow', new UntypedFormControl(false));
      _.set(model, 'primary', new UntypedFormControl(item.default === 1));
      _.set(model, 'secondary', new UntypedFormControl(item.default === 2));
      return this.fb.group(model);
    });
    this.form.setControl('priceSheets', this.fb.array(formGroups));
  }

    getError(): string {
      let error = '';
      const priceSheetNumbers = _.map(this.priceSheets.controls, (formGroup: UntypedFormGroup) => {
        return formGroup.get('priceSheetNumber').value;
      });
      const hasDuplicates = _.uniq(priceSheetNumbers).length !== priceSheetNumbers.length;
      if (hasDuplicates) { error = 'Price Sheets cannot have the same Price Sheet Number.'; }
      const hasNewInvalidNumbers = _.chain(this.priceSheets.controls)
        .filter((formGroup: UntypedFormGroup) => formGroup.get('isNewRow').value)
        .some((formGroup: UntypedFormGroup) => formGroup.get('priceSheetNumber').invalid)
        .value();
      if (hasNewInvalidNumbers) {
        const maxLength: number = _.get(this.getFieldByMap('priceSheetNumber'), 'validation.PIES_6_5.maxLength', 15);
        return `Price Sheet Number must be provided and is limited to ${maxLength} characters`;
      }
      return error;
    }

  public fieldValidation(item: any, formGroup: UntypedFormGroup): any {
    if (!item) {
      return;
    }
    let validation = super.fieldValidation(item, formGroup);
    // PIES 6.7 validation row was added for test
    const pies65: any = _.find(validation, {name: 'PIES_6_5'});
    if (pies65) {
      validation = [...validation, {
        key: 'pies67',
        title: 'PIES 6.7',
        valid: pies65.valid,
      }];
    }
    return validation;
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

   private getControlValue(formGroup: UntypedFormGroup, fieldName: string) {
    const control = formGroup.controls ? formGroup.controls[fieldName] : null;
    return control ? control.value : '';
   }

   private getDefaultGhostAttr(formGroup: UntypedFormGroup) {
    const isPrimary = this.getControlValue(formGroup, 'primary');
    const isSecondary = this.getControlValue(formGroup, 'secondary');
    if (isPrimary || isSecondary) {
      return `${isPrimary ? '1' : '2'}`;
    }
    return null;
  }

  private setFormDateErrors() {
    _.each(this.priceSheets.controls, (group: UntypedFormGroup, index: number) => {
      this.setDateDisabledRange(
        group.get('expirationDate').value,
        'effective_date',
        index,
      );
      this.setDateAheadControlError(
        group.get('effectiveDate'),
        group.get('expirationDate'),
      );
    });
  }

  public fieldHintText(name: string): string {
    const item = this.getField(name);
    return item && item.hint_detail;
  }

  protected getTitle(name: string): string {
    // find title in rowData
    // @ts-ignore
    const colData = _.find(this.rowData, { fieldName: name });
    if (_.get(colData, 'title')) {
      return colData.title;
    }

    // find title in segmentData
    // @ts-ignore
    const item = _.find(this.segmentData, { field_map: name });
    return item ? item.title : '';
  }

  public getFieldLookup(fieldMap: string): any[] {
    const field: any = this.getField(fieldMap);
    return field ? field.lookup : null;
  }

  protected getInvalidDot(fieldMap: string, formGroup: UntypedFormGroup, index: number) {
    const control = _.get(formGroup, `controls.${fieldMap}`);
    if (control.invalid) {
      return   this.sanitizer.bypassSecurityTrustHtml(`
        <div class="circle-icon size-small cell-invalid-dot circle-red"
        data-ghost="cell-invalid-dot-${fieldMap}-${index}"></div>
      `);
    } else {
      return null;
    }
  }

  protected collapseRow(index: number, collapse?: boolean) {
    // pass -1 to collapse all
    const collapseGroupsArr: any = this.collapseGroups.toArray();

    if (!_.get(collapseGroupsArr, 'length') || !_.get(collapseGroupsArr, `[${index}]`)) { return; }
    if (_.isNil(collapse)) { collapse = !collapseGroupsArr[index].collapsed; }
    collapseGroupsArr[index].setCollapsed(collapse);

    this.ref.markForCheck();
  }

  onRowRemove(formGroup: UntypedFormGroup) {
    const index = this.formArray.controls.indexOf(formGroup);
    this.formArray.removeAt(index);
    this.data.splice(index, 1);
    this.formArray.markAsDirty();
  }

  onCollapseClick(index: number) {
    this.collapseRow(index);
  }

  onViewEditSaveClick() {
    if (this.isFormChanged) {
      this.saveChanges.emit();
    } else {
      this.toggleEditMode();
    }
  }

  onCancelClick() {
    this.cancel.emit();
  }

  onRowAdd() {
    this.onAddNewPriceSheetClick();
  }
}
